import * as React from "react";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useIdentity } from "../../containers/IdentityContainer";
import { useLocationsState } from "../../containers/LocationsStateContainer";
import { useTenants } from "../../hooks/useTenants";
import { AutocompleteAircraft } from "../../screens/Settings/HangarSettings/AutocompleteAircraft";
import { Hangar, Location, Ramp, Tenant } from "../../types";
import { isManagerLevel } from "../../utils/permissions";
import { ConfirmationDialog } from "../ConfirmationDialog";
import { LocationTextField } from "../LocationTextField";
import { SelectTailNumber } from "../SelectTailNumber";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { formatDistance } from "date-fns";
import { addTenantConformationText } from "../../utils/phrasing";

type Props = {
  displayTypeAndLocation: boolean;
  isReference: boolean;
  tenant: Tenant;
  setTenant: (t: Tenant) => void;
  currentLocation: Location<Ramp | Hangar>;
  setCurrentLocation: (l: Location<Ramp | Hangar>) => void;
  assignedLocation: Location<Ramp | Hangar>;
  setAssignedLocation: (l: Location<Ramp | Hangar>) => void;
  onSave: () => Promise<void>;
  onClose: () => void;
};

export const EditTenantPresenter: React.FC<Props> = ({
  displayTypeAndLocation,
  tenant,
  setTenant,
  currentLocation,
  setCurrentLocation,
  assignedLocation,
  setAssignedLocation,
  onSave,
  onClose,
}) => {
  const { activeFBO } = useActiveFBOs();
  const { airplxIdentity } = useIdentity();
  const { locations } = useLocationsState();
  const { tenants } = useTenants();
  const flashTimeoutRef = React.useRef<number | null>(null);
  const [shouldFlashAircraft, setShouldFlashAircraft] =
    React.useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = React.useState<{
    text: string;
    onOk: () => void;
  }>({
    text: "",
    onOk: () => {
      //
    },
  });
  const handleClose = () => {
    onClose();
  };

  const isDuplicateTailNumber = React.useMemo(() => {
    return Boolean(
      tenants
        .filter((t) => t.tail_number)
        .find(
          (t) =>
            t.id !== tenant.id &&
            t.tail_number.toLocaleUpperCase() ===
              tenant.tail_number.toLocaleUpperCase()
        )
    );
  }, [tenants, tenant]);

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      maxWidth="lg"
      data-element="edit_tenant"
    >
      <DialogTitle>Edit Aircraft</DialogTitle>
      <DialogContent sx={{ width: 500 }}>
        <Stack direction="column" spacing={2} sx={{ mt: 1, width: "100%" }}>
          <AutocompleteAircraft
            label={"Select an Aircraft"}
            onSelect={(aircraft) => {
              setTenant({ ...tenant, aircraft_id: aircraft.id, aircraft });
            }}
            defaultSelected={tenant.aircraft}
            shouldFlash={shouldFlashAircraft}
          />
          <Stack direction="row" spacing={2}>
            <SelectTailNumber
              disableAutocomplete={false}
              warning={isDuplicateTailNumber && "Duplicate tail number"}
              tenants={tenants}
              defaultValue={tenant.tail_number}
              onInputChange={(tailNumber: string) => {
                setTenant({ ...tenant, tail_number: tailNumber });
              }}
              onChange={(selectedTenant) => {
                if (selectedTenant?.aircraft) {
                  setTenant({
                    ...tenant,
                    tail_number: selectedTenant.tail_number,
                    aircraft_id: selectedTenant.aircraft.id,
                    aircraft: selectedTenant.aircraft,
                  });

                  // Trigger flash animation when user selects from tail number dropdown
                  setShouldFlashAircraft(true);

                  // Reset flash state after the animation completes
                  if (flashTimeoutRef.current !== null) {
                    clearTimeout(flashTimeoutRef.current);
                  }
                  flashTimeoutRef.current = window.setTimeout(() => {
                    setShouldFlashAircraft(false);
                    flashTimeoutRef.current = null;
                  }, 1500);
                }
              }}
            />
            <Stack direction="column" sx={{ width: "100%" }}>
              <TextField
                inputProps={{ testid: "owner_manager" }}
                size="small"
                fullWidth
                label="Owner/Manager"
                onChange={(evt) =>
                  setTenant({ ...tenant, owner_manager: evt.target.value })
                }
                value={tenant.owner_manager}
              />
              <Typography variant="caption" color="error"></Typography>
            </Stack>
          </Stack>

          <Stack direction="column" spacing={1}>
            <TextField
              inputProps={{ testid: "note" }}
              size="small"
              fullWidth
              multiline
              rows={4}
              label="Notes"
              placeholder="Add notes about this aircraft..."
              onChange={(evt) =>
                setTenant({
                  ...tenant,
                  note: evt.target.value,
                  note_last_updated: new Date().toISOString(),
                  note_last_updated_by: airplxIdentity?.email ?? "Unknown",
                })
              }
              value={tenant.note || ""}
            />
            {Boolean(tenant.note && tenant.note_last_updated) && (
              <Typography variant="body2" color="text.secondary">
                Last update:{" "}
                {formatDistance(new Date(tenant.note_last_updated), new Date(), {
                  addSuffix: true,
                })}{" "}
                ({new Date(tenant.note_last_updated).toLocaleDateString()}) by{" "}
                {tenant.note_last_updated_by}
              </Typography>
            )}
          </Stack>

          {displayTypeAndLocation && (
            <>
              <Stack direction="row" spacing={2}>
                <TextField
                  disabled={!isManagerLevel(activeFBO)}
                  select
                  size="small"
                  fullWidth
                  label="Type"
                  onChange={(evt) => {
                    if (evt.target.value === "transient") {
                      setShowConfirmation({
                        text: "Making this Base Tenant into a Transient means this aircraft is no longer based in your hangars. It will remain on your transient list of aircraft.",
                        onOk: () => {
                          setTenant({
                            ...tenant,
                            type: evt.target.value,
                          }),
                            setAssignedLocation(null);
                        },
                      });

                      return;
                    }
                    if (evt.target.value === "base") {
                      setShowConfirmation({
                        text: addTenantConformationText(
                          currentLocation ? "hangar" : null
                        ),
                        onOk: () =>
                          setTenant({ ...tenant, type: evt.target.value }),
                      });
                      return;
                    }
                  }}
                  value={tenant.type}
                  sx={{ height: "100%" }}
                >
                  <MenuItem value="base">Tenant</MenuItem>
                  <MenuItem value="transient">Transient</MenuItem>
                </TextField>
                <Stack direction="column" spacing={2} sx={{ width: "100%" }}>
                  <LocationTextField
                    disabled={
                      !isManagerLevel(activeFBO) ||
                      tenant.type !== "base"
                    }
                    isReference={true}
                    unassignedText={
                      tenant.type === "base" ? "Unassigned" : "N/A"
                    }
                    selectedLocationId={assignedLocation?.location_id}
                    onChange={(newLocationId) => {
                      const newLocation = locations.find(
                        (l) => l.location_id === newLocationId
                      );
                      setAssignedLocation(newLocation);
                    }}
                  />
                  <LocationTextField
                    isReference={false}
                    selectedLocationId={currentLocation?.location_id}
                    unassignedText="Not Hangared"
                    onChange={(newLocationId) => {
                      const newLocation = locations.find(
                        (l) => l.location_id === newLocationId
                      );
                      setCurrentLocation(newLocation);
                    }}
                  />
                </Stack>
              </Stack>
            </>
          )}
        </Stack>
        {Boolean(showConfirmation?.text) && (
          <ConfirmationDialog
            title="Please Confirm"
            text={showConfirmation?.text}
            onCancel={() => {
              // do nothing
            }}
            onOk={() => showConfirmation?.onOk()}
            onClose={() => {
              setShowConfirmation({
                text: "",
                onOk: () => {
                  //
                },
              });
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          disabled={isDuplicateTailNumber}
          variant="contained"
          color="success"
          onClick={async () => {
            await onSave();
            handleClose();
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
